import flatten from "lodash-es/flatten";
import { Reducer } from "redux";

import {
    STROLLER_ALL_REVIEWS_PHOTOS_FETCH_FAILURE,
    STROLLER_ALL_REVIEWS_PHOTOS_FETCH_REQUEST,
    STROLLER_ALL_REVIEWS_PHOTOS_FETCH_SUCCESS,
    STROLLER_ALL_REVIEWS_PHOTOS_UPDATE_THUMBS,
    StrollerAllReviewsPhotosFetchFailureAction,
    StrollerAllReviewsPhotosFetchRequestAction,
    StrollerAllReviewsPhotosFetchSuccessAction,
    StrollerAllReviewsPhotosUpdateThumbsAction,
} from "@/mk2/apps/strollers/containers/StrollerAllReviewsPhotos/StrollerAllReviewsPhotos.actions";
import { tupdate } from "@/mk2/helpers/types";
import { errorMessage } from "@/mk2/reducers/errorMessage";
import { LoadingState, loadingState } from "@/mk2/reducers/loadingState";
import { StrollerUserReviewEntity, StrollerUserReviewSchema } from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";

export interface StrollerAllReviewsPhotosState {
    loadingState: LoadingState;
    errorMessage: string;
    strollerId: number | null;
    strollerReviewIds: number[];
    strollerReviewPhotoIds: number[];
}

export const initialStrollerAllReviewsPhotosState: StrollerAllReviewsPhotosState = {
    loadingState: LoadingState.INIT,
    errorMessage: null,
    strollerId: null,
    strollerReviewIds: [],
    strollerReviewPhotoIds: [],
};

export interface StrollersAllReviewsPhotosState {
    pages: {
        [strollerSlug: string]: StrollerAllReviewsPhotosState;
    };
    hideThumbnails: boolean;
    thumbsKey: string;
}

export const initialStrollersAllReviewsPhotosState: StrollersAllReviewsPhotosState = {
    pages: {},
    hideThumbnails: null,
    thumbsKey: new Date().toISOString(),
};

declare type StrollerAllReviewsPhotosReducerAction =
    | StrollerAllReviewsPhotosFetchRequestAction
    | StrollerAllReviewsPhotosFetchSuccessAction
    | StrollerAllReviewsPhotosFetchFailureAction
    | StrollerAllReviewsPhotosUpdateThumbsAction;

export const strollersAllReviewsPhotosReducer: Reducer<
    StrollersAllReviewsPhotosState,
    StrollerAllReviewsPhotosReducerAction
> = (state = initialStrollersAllReviewsPhotosState, action) => {
    switch (action.type) {
        case STROLLER_ALL_REVIEWS_PHOTOS_FETCH_REQUEST:
        case STROLLER_ALL_REVIEWS_PHOTOS_FETCH_FAILURE: {
            const strollerSlug = action.strollerSlug;
            const photosState = state.pages[strollerSlug] || initialStrollerAllReviewsPhotosState;
            return tupdate(state, {
                pages: tupdate(state.pages, {
                    [strollerSlug]: tupdate(photosState, {
                        loadingState: loadingState(undefined, action),
                        errorMessage: errorMessage(undefined, action),
                    }),
                }),
            });
        }

        case STROLLER_ALL_REVIEWS_PHOTOS_FETCH_SUCCESS: {
            const strollerSlug = action.strollerSlug;
            const photosState = state.pages[strollerSlug] || initialStrollerAllReviewsPhotosState;
            return tupdate(state, {
                pages: tupdate(state.pages, {
                    [strollerSlug]: tupdate(photosState, {
                        loadingState: loadingState(undefined, action),
                        errorMessage: errorMessage(undefined, action),
                        strollerId: action.response.result.stroller,
                        strollerReviewIds: action.response.result.strollerReviews,
                        // collect all review photos
                        strollerReviewPhotoIds: flatten(
                            getDenormalizedEntities<StrollerUserReviewEntity>(
                                action.response,
                                StrollerUserReviewSchema,
                                action.response.result.strollerReviews,
                            ).map((review) => review.photos),
                        ).map((photo) => photo.id),
                    }),
                }),
            });
        }

        case STROLLER_ALL_REVIEWS_PHOTOS_UPDATE_THUMBS: {
            return tupdate(state, {
                hideThumbnails: action.hide,
                thumbsKey: new Date().toISOString(),
            });
        }

        default:
            return state;
    }
};
